// src/pages/Home.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import toast from "react-hot-toast";

import AddSale from "../components/AddSale";
import SalesList from "../components/SalesList";
import AddUser from "./addUser";
import UsersList from "../components/UsersList";
import { differenceInCalendarDays, isAfter, isBefore } from "date-fns";
import { FaCalendarAlt } from "react-icons/fa";

import {
  FaUsers,
  FaBalanceScale,
  FaHome,
  FaUserShield,
  FaPlus,
  FaDownload,
  FaSignOutAlt,
  FaPlusCircle,
  FaBars,
  FaTimes,
} from "react-icons/fa";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);
const SHEET_PDF_JSON_URL =
  "https://script.google.com/macros/s/AKfycbzR9X4SN8qlLGVPYoZRx-_16siw8h9wr8x9CTT78N47GCR9OX8FoYk1SKq8Xe7Zc_qX/exec";

const getLoginDealerGst = () => {
  try {
    const raw = localStorage.getItem("dealer");
    if (!raw) return "";
    const obj = JSON.parse(raw);
    return String(obj?.gst || "")
      .trim()
      .toUpperCase();
  } catch {
    return "";
  }
};

// TIMESTAMP format: "04/02/2026 05:36:10" (DD/MM/YYYY HH:mm:ss)
const tsToMs = (s) => {
  const m = String(s || "")
    .trim()
    .match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (!m) return 0;
  const [, dd, mm, yyyy, HH, MM, SS] = m;
  return new Date(`${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`).getTime();
};

// ===================== OFFER LOGIC =====================
const offerTiers = [
  { minKg: 3000, name: "Cooker / Blender / Iron" },
  { minKg: 6000, name: "Induction / Mixture Grinder / Smart Watch" },
  { minKg: 10000, name: "Luggage Bag / Blanket Set" },
  { minKg: 18000, name: "Home Theater / Kitchen Combo" },
  { minKg: 25000, name: "Android Phone" },
  { minKg: 32000, name: "Tablet / Microwave / Kitchen Gas Stove Combo" },
  { minKg: 40000, name: "Ghar Ghanti / Cooler / Tablet + Smart Watch" },
  { minKg: 50000, name: "Smart Phone / Washing Machine / RO / Premium Gift" },
  {
    minKg: 75000,
    name: "Fridge / Smartphone + Home Theater / RO + Kitchen Combo / Premium Gift",
  },
  { minKg: 100000, name: "AC / Smart TV / Smartphone / Premium Gift" },
];

const ALLOWED_VIEWS = ["dashboard", "users", "sales", "addUser"];

export default function Home() {
  const navigate = useNavigate();

  // Load last view from localStorage, fallback to "dashboard"
  const [currentView, setCurrentView] = useState(() => {
    const savedView = localStorage.getItem("lastDealerView");
    return savedView && ALLOWED_VIEWS.includes(savedView)
      ? savedView
      : "dashboard";
  });

  const [editingUser, setEditingUser] = useState(null);
  const [preSelectedUser, setPreSelectedUser] = useState(null);
  const [dealerData, setDealerData] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [showAddSale, setShowAddSale] = useState(false);

  const [usersForSale, setUsersForSale] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [totalPurchase, setTotalPurchase] = useState(0);

  const isAddNewUserMode = currentView === "addUser" && !editingUser;
  const isEditUserMode = currentView === "addUser" && !!editingUser;
  const [dealerRewards, setDealerRewards] = useState([]);
  const [sheetPdfUrl, setSheetPdfUrl] = useState("");

  // Save current view to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("lastDealerView", currentView);
  }, [currentView]);

  const openAddSaleFromSidebar = () => {
    setUsersForSale(dealerData?.users || []);
    setPreSelectedUser(null);
    setShowAddSale(true);
    setSidebarOpen(false);
  };

  const target = dealerData?.target || null;
  const hasTargetWindow = Boolean(target?.startDate && target?.endDate);

  const targetStart = hasTargetWindow ? new Date(target.startDate) : null;
  const targetEnd = hasTargetWindow ? new Date(target.endDate) : null;

  if (targetStart) targetStart.setHours(0, 0, 0, 0);
  if (targetEnd) targetEnd.setHours(23, 59, 59, 999);

  const targetInfo = useMemo(() => {
    if (!dealerData?.target?.startDate || !dealerData?.target?.endDate)
      return null;

    const start = new Date(dealerData.target.startDate);
    const end = new Date(dealerData.target.endDate);
    const today = new Date();

    let remainingDays = 0;
    let status = "upcoming";

    if (isBefore(today, start)) {
      remainingDays = differenceInCalendarDays(end, start);
      status = "upcoming";
    } else if (isAfter(today, end)) {
      remainingDays = 0;
      status = "completed";
    } else {
      remainingDays = differenceInCalendarDays(end, today);
      status = "active";
    }

    let months = 0;
    let days = remainingDays;

    if (remainingDays >= 30) {
      months = Math.floor(remainingDays / 30);
      days = remainingDays % 30;
    }

    return { start, end, remainingDays, status, months, days };
  }, [dealerData?.target]);

  const isAdmin = dealerData.type === "admin";

  const fetchSales = async () => {
    try {
      const res = await api.get("/sales");
      setSales(res.data || []);
    } catch (err) {
      // toast.error("Failed to fetch sales");
    }
  };

  const openAddSaleForUser = (user) => {
    setUsersForSale([user]);
    setPreSelectedUser(user);
    setShowAddSale(true);
  };

  const fetchDealerDashboard = async () => {
    try {
      const storedDealer = JSON.parse(localStorage.getItem("dealer") || "{}");
      if (!storedDealer || !storedDealer.mobile) {
        toast.error("Please login again");
        navigate("/");
        return;
      }

      const res = await api.get("/dealer", {
        headers: { "x-auth-phone": storedDealer.mobile },
      });

      if (res.data.success) {
        const {
          dealer,
          sales: fetchedSales,
          totalUsers: usersCount,
          totalSalesKG,
        } = res.data;

        setDealerData(dealer);
        setTotalUsers(usersCount);
        setTotalSales(totalSalesKG || 0);
        setUsersForSale(dealer.users || []);
        setSales(fetchedSales || []);

        // ── FETCH DEALER REWARDS ──
        try {
          const rewardRes = await api.get(
            `/admin/dealers/${dealer._id}/reward`,
          );
          if (
            rewardRes.data?.success &&
            Array.isArray(rewardRes.data.rewards)
          ) {
            setDealerRewards(rewardRes.data.rewards);
          } else {
            setDealerRewards([]);
          }
        } catch (err) {
          console.error("Failed to fetch dealer rewards", err);
          setDealerRewards([]);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealerDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const fetchPdfByGst = async () => {
      const gst =
        getLoginDealerGst() ||
        String(dealerData?.gst || "")
          .trim()
          .toUpperCase();

      if (!gst) {
        setSheetPdfUrl("");
        return;
      }

      try {
        console.log("🔎 Matching GST:", gst);

        const res = await fetch(SHEET_PDF_JSON_URL);
        const rows = await res.json();

        if (!Array.isArray(rows)) {
          setSheetPdfUrl("");
          return;
        }

        // ✅ GST match
        const match = rows.find(
          (r) =>
            String(r.GST || "")
              .trim()
              .toUpperCase() === gst,
        );

        console.log("📄 Sheet match row:", match);

        setSheetPdfUrl(match?.PDF || "");
      } catch (e) {
        console.error("Sheet PDF fetch error:", e);
        setSheetPdfUrl("");
      }
    };

    fetchPdfByGst();
  }, [dealerData?.gst]);

  useEffect(() => {
    const loadDealerPdfFromSheet = async () => {
      const gst = getLoginDealerGst();
      if (!gst) {
        setSheetPdfUrl("");
        return;
      }

      try {
        const res = await fetch(SHEET_PDF_JSON_URL);
        if (!res.ok) throw new Error("Sheet JSON failed: " + res.status);

        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];

        const matches = rows.filter(
          (r) =>
            String(r?.GST || "")
              .trim()
              .toUpperCase() === gst,
        );

        if (!matches.length) {
          setSheetPdfUrl("");
          return;
        }

        // ✅ Pick latest by TIMESTAMP
        matches.sort((a, b) => tsToMs(b?.TIMESTAMP) - tsToMs(a?.TIMESTAMP));

        const pdf = String(matches[0]?.PDF || "").trim();
        setSheetPdfUrl(pdf);
      } catch (err) {
        console.error("Failed to load PDF from sheet:", err);
        setSheetPdfUrl("");
      }
    };

    loadDealerPdfFromSheet();
  }, []);

  // Lock body scroll when sidebar open (mobile)
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [sidebarOpen]);

  // TOTAL PURCHASE logic
  useEffect(() => {
    const parseNum = (v) => {
      if (v == null) return 0;
      const n = Number(String(v).replace(/,/g, "").trim());
      return Number.isFinite(n) ? n : 0;
    };

    const fetchGstAndPurchase = async () => {
      const gst = String(dealerData?.gst || dealerData?.GST || "").trim();
      if (!gst) {
        setTotalPurchase(0);
        setDealerData((prev) => ({
          ...prev,
          totalPurchase: 0,
        }));
        return;
      }

      try {
        const res = await fetch(
          "https://script.google.com/macros/s/AKfycbxiM6gYXIcHWsg_hzejg_MxGPdXHhhlxlcDB6s-48KEqpMTVUkxd7Qx1T4q0-Z2y101zQ/exec",
        );
        const data = await res.json();

        const hasTargetWindow = Boolean(
          dealerData?.target?.startDate && dealerData?.target?.endDate,
        );

        const startDate = hasTargetWindow
          ? new Date(dealerData.target.startDate)
          : null;
        const endDate = hasTargetWindow
          ? new Date(dealerData.target.endDate)
          : null;

        if (hasTargetWindow) {
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
        }

        const gstMatches = (Array.isArray(data) ? data : []).filter(
          (row) => String(row?.GSTIN || "").trim() === gst,
        );

        const windowMatches = hasTargetWindow
          ? gstMatches.filter((row) => {
              const t = row?.TimeSatmp;
              if (!t) return false;
              const saleDate = new Date(t);
              if (Number.isNaN(saleDate.getTime())) return false;
              return saleDate >= startDate && saleDate <= endDate;
            })
          : gstMatches;

        const totalQty = windowMatches.reduce(
          (sum, item) => sum + parseNum(item?.["Current Dispt Qty"]),
          0,
        );

        setTotalPurchase(totalQty);

        setDealerData((prev) => ({
          ...prev,
          totalPurchase: totalQty,
        }));
      } catch (err) {
        console.error("Failed to fetch GST data:", err);
        setTotalPurchase(0);
        setDealerData((prev) => ({
          ...prev,
          totalPurchase: 0,
        }));
      }
    };

    fetchGstAndPurchase();
  }, [
    dealerData?.gst,
    dealerData?.GST,
    dealerData?.target?.startDate,
    dealerData?.target?.endDate,
  ]);

  const areaWiseSales = useMemo(() => {
    const acc = sales.reduce((out, sale) => {
      const area = sale.area || "Unknown";
      if (!out[area]) out[area] = { totalKg: 0, dealers: new Set() };
      out[area].totalKg += Number(sale.kg || 0);
      if (sale.dealerName) out[area].dealers.add(sale.dealerName);
      return out;
    }, {});

    return Object.entries(acc)
      .map(([area, data]) => ({
        area,
        totalKg: data.totalKg,
        dealers: Array.from(data.dealers),
      }))
      .sort((a, b) => (b.totalKg || 0) - (a.totalKg || 0));
  }, [sales]);

  // Pagination (kept even if not currently rendered — might be used in SalesList view)
  const [salesPage, setSalesPage] = useState(1);
  const salesRowsPerPage = 10;
  const salesTotalPages = Math.max(
    1,
    Math.ceil(areaWiseSales.length / salesRowsPerPage),
  );

  useEffect(() => setSalesPage(1), [areaWiseSales.length]);
  useEffect(() => {
    if (salesPage > salesTotalPages) setSalesPage(salesTotalPages);
  }, [salesPage, salesTotalPages]);

  const logout = () => {
    localStorage.clear();
    toast.success("Logged out successfully");
    navigate("/");
  };

  const toggleSidebar = () => setSidebarOpen((p) => !p);

  const handleEditUserFromList = (user) => {
    setEditingUser(user);
    setCurrentView("addUser");
    setSidebarOpen(false);
  };

  const openAddNewUser = () => {
    setEditingUser(null);
    setCurrentView("addUser");
    setSidebarOpen(false);
  };

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-xl sm:text-2xl text-gray-600">
          Loading dashboard...
        </p>
      </div>
    );

  const dealerName =
    dealerData.contact_person ||
    dealerData.partyName ||
    dealerData.name ||
    "Dealer";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar & Mobile Menu */}
      <button
        onClick={toggleSidebar}
        className="fixed z-[60] lg:hidden top-3 left-3 bg-white text-yellow-700 border border-gray-200 h-11 w-11 rounded-xl grid place-items-center active:scale-95 transition shadow-lg"
        title="Menu"
        aria-label="Open menu"
      >
        {sidebarOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
      </button>

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 sm:w-72 bg-white text-gray-900 flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="p-5 sm:p-6 min-md:pt-14 sm:pt-6 border-b border-yellow-500 bg-white text-yellow-600">
          <div className="py-5 flex items-center justify-center">
            <img
              src="/images/logo.png"
              alt="Logo"
              className="h-16 w-auto object-contain cursor-pointer"
              onClick={() => setCurrentView("dashboard")}
            />
          </div>

          <p className="mt-2.5 text-sm opacity-90 truncate flex justify-center items-left">
            Welcome, <strong>{dealerName}</strong>
          </p>
          {isAdmin && (
            <p className="mt-2 text-xs bg-yellow-200 border border-yellow-600 text-black font-bold px-3 py-1 rounded-full inline-block flex justify-center items-left">
              Admin Access
            </p>
          )}
        </div>

        <nav className="flex-1 p-3 sm:p-4 space-y-2 sm:space-y-3 overflow-y-auto bg-white">
          <button
            onClick={() => {
              setCurrentView("dashboard");
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left transition-all ${
              currentView === "dashboard"
                ? "bg-yellow-600 text-white shadow-lg"
                : "hover:bg-yellow-200 hover:text-yellow-700"
            }`}
          >
            <FaHome size={18} /> <span className="font-medium">Dashboard</span>
          </button>

          <button
            onClick={openAddNewUser}
            className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left transition-all ${
              isAddNewUserMode
                ? "bg-yellow-600 text-white shadow-lg"
                : "hover:bg-yellow-200 hover:text-yellow-700"
            }`}
          >
            <FaPlus size={18} />{" "}
            <span className="font-medium">Add New Customer</span>
          </button>

          <button
            onClick={() => {
              setCurrentView("users");
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left transition-all ${
              currentView === "users" || isEditUserMode
                ? "bg-yellow-600 text-white shadow-lg"
                : "hover:bg-yellow-200 hover:text-yellow-700"
            }`}
          >
            <FaUsers size={18} />{" "}
            <span className="font-medium">My Customers</span>
          </button>

          <button
            onClick={() => {
              setCurrentView("sales");
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left transition-all ${
              currentView === "sales"
                ? "bg-yellow-600 text-white shadow-lg"
                : "hover:bg-yellow-200 hover:text-yellow-700"
            }`}
          >
            <FaBalanceScale size={18} />{" "}
            <span className="font-medium">Customers Sales</span>
          </button>

          <button
            onClick={openAddSaleFromSidebar}
            className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left transition-all hover:bg-yellow-200 hover:text-yellow-700"
          >
            <FaPlusCircle size={18} />
            <span className="font-medium">Add Sale</span>
          </button>

          {isAdmin && (
            <button
              onClick={() => navigate("/admin-dashboard")}
              className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left transition-all lg:hidden hover:bg-yellow-400 hover:text-white"
              title="Admin Dashboard"
            >
              <FaUserShield />{" "}
              <span className="font-medium">Admin Dashboard</span>
            </button>
          )}

          <div className="mt-auto pt-4 sm:pt-6 border-t border-yellow-700 lg:hidden">
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl text-left hover:bg-red-600 hover:text-white transition-all"
            >
              <FaSignOutAlt size={18} />{" "}
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </nav>
      </aside>

      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}

      {/* Main Content */}
      <main className="min-h-screen lg:ml-72">
        <div className="px-3 sm:px-6 lg:px-8 pt-[3rem] sm:pt-18 lg:pt-8 pb-6 sm:pb-8">
          {/* Top bar */}
          <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {currentView === "dashboard" && targetInfo && (
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-4 lg:gap-6 w-full lg:w-auto">
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 sm:px-5 sm:py-4 flex items-start gap-2 sm:gap-3">
                  <FaCalendarAlt
                    className="text-blue-600 mt-0.5 sm:mt-1 flex-shrink-0"
                    size={14}
                  />
                  <div className="flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-semibold text-gray-600">
                      Target Period
                    </p>
                    <p className="text-[11px] sm:text-base lg:text-lg font-bold text-gray-900 leading-tight mt-0.5 sm:mt-1">
                      {targetInfo.start.toLocaleDateString("en-IN")} –{" "}
                      {targetInfo.end.toLocaleDateString("en-IN")}
                    </p>
                  </div>
                </div>

                <div
                  className={`border rounded-lg px-3 py-2 sm:px-5 sm:py-4 flex items-start gap-2 sm:gap-3 ${
                    targetInfo.status === "completed"
                      ? "bg-red-50 border-red-200"
                      : targetInfo.status === "upcoming"
                        ? "bg-gray-50 border-gray-200"
                        : "bg-green-50 border-green-200"
                  }`}
                >
                  <FaCalendarAlt
                    className="text-gray-600 mt-0.5 sm:mt-1 flex-shrink-0"
                    size={14}
                  />
                  <div className="flex flex-col justify-center">
                    <p className="text-[10px] sm:text-xs font-semibold text-gray-600">
                      Remaining Days
                    </p>
                    <p className="text-[12px] sm:text-lg lg:text-xl font-extrabold text-gray-900 mt-0.5 sm:mt-1">
                      {targetInfo.status === "completed"
                        ? "Completed"
                        : `${targetInfo.months > 0 ? `${targetInfo.months}M ` : ""}${
                            targetInfo.days
                          }D`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 sm:gap-4 ml-auto max-md:hidden">
              {isAdmin && (
                <button
                  onClick={() => navigate("/admin-dashboard")}
                  className="h-10 px-4 sm:px-5 rounded-xl bg-white border shadow-sm hover:bg-gray-50 transition font-semibold flex items-center gap-2 whitespace-nowrap"
                  title="Admin Dashboard"
                >
                  <FaUserShield size={18} />
                  <span className="hidden sm:inline">Admin Dashboard</span>
                </button>
              )}

              <button
                onClick={logout}
                className="h-10 w-10 sm:w-auto sm:px-5 rounded-xl bg-red-600 text-white hover:bg-red-700 transition shadow-sm font-semibold flex items-center justify-center gap-2"
                title="Logout"
              >
                <FaSignOutAlt size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>

          {/* Dashboard */}
          {currentView === "dashboard" && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-yellow-200 rounded-xl shadow-sm border border-yellow-600 p-3 sm:p-4 flex items-center gap-3 hover:shadow-md transition">
                  <div className="hidden sm:grid h-10 w-10 place-items-center bg-yellow-700 rounded-lg text-white">
                    <FaUsers size={18} />
                  </div>
                  <div className="leading-tight">
                    <p className="text-[11px] sm:text-xs text-black-100 font-semibold">
                      Customers
                    </p>
                    <p className="text-sm sm:text-lg font-extrabold text-black">
                      {totalUsers ?? 0}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-200 rounded-xl shadow-sm border border-yellow-600 p-3 sm:p-4 flex items-center gap-3 hover:shadow-md transition">
                  <div className="hidden sm:grid h-10 w-10 place-items-center bg-yellow-700 rounded-lg text-white">
                    <FaBalanceScale size={18} />
                  </div>
                  <div className="leading-tight">
                    <p className="text-[11px] sm:text-xs text-black-100 font-semibold">
                      Sales
                    </p>
                    <p className="text-sm sm:text-lg font-extrabold text-black">
                      {Number(totalSales || 0).toLocaleString()} KG
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-200 rounded-xl shadow-sm border border-yellow-600 p-3 sm:p-4 flex items-center gap-3 hover:shadow-md transition">
                  <div className="hidden sm:grid h-10 w-10 place-items-center bg-yellow-700 rounded-lg text-white">
                    <FaBalanceScale size={18} />
                  </div>
                  <div className="leading-tight">
                    <p className="text-[11px] sm:text-xs text-black-100 font-semibold">
                      Purchase
                    </p>
                    <p className="text-sm sm:text-lg font-extrabold text-black">
                      {Math.floor(totalPurchase || 0).toLocaleString()} KG
                    </p>
                  </div>
                </div>
              </div>

              {/* Vyapar Sales — business coming in through the SOTA app.
                  Customers a dealer adds in the SOTA "My Customers" page land
                  in the SAME shared Dealers collection with source:"sota", so
                  we surface them here as a dedicated Vyapar Sales summary. */}
              {(() => {
                const allUsers = Array.isArray(dealerData?.users)
                  ? dealerData.users
                  : [];
                const sotaUsers = allUsers.filter(
                  (u) => String(u?.source || "").toLowerCase() === "sota"
                );
                const sotaSalesKg = sotaUsers.reduce(
                  (sum, u) =>
                    sum +
                    (Array.isArray(u?.sales)
                      ? u.sales.reduce(
                          (s, x) => s + (Number(x?.quantity) || 0),
                          0
                        )
                      : 0),
                  0
                );
                return (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-2xl shadow-sm border border-yellow-500 p-4 sm:p-5 mb-4 sm:mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 flex items-center gap-2">
                        <FaBalanceScale className="text-yellow-700" /> Vyapar
                        Sales
                      </h2>
                      <span className="text-[11px] sm:text-xs font-bold bg-yellow-700 text-white px-2.5 py-1 rounded-full">
                        via SOTA App
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/70 rounded-xl p-3 border border-yellow-300">
                        <p className="text-[11px] sm:text-xs text-gray-600 font-semibold">
                          SOTA Customers
                        </p>
                        <p className="text-lg sm:text-2xl font-extrabold text-gray-900">
                          {sotaUsers.length}
                        </p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-3 border border-yellow-300">
                        <p className="text-[11px] sm:text-xs text-gray-600 font-semibold">
                          SOTA Sales
                        </p>
                        <p className="text-lg sm:text-2xl font-extrabold text-gray-900">
                          {sotaSalesKg.toLocaleString()} KG
                        </p>
                      </div>
                    </div>
                    {sotaUsers.length > 0 && (
                      <button
                        onClick={() => setCurrentView("users")}
                        className="mt-3 w-full text-sm font-bold text-yellow-800 hover:text-yellow-900 underline"
                      >
                        View SOTA customers →
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Reward Ladder Table */}
              <div className="bg-white rounded-2xl shadow-sm border p-5">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Reward Details
                  </h2>

                  {sheetPdfUrl && (
                    <button
                      onClick={() => window.open(sheetPdfUrl, "_blank")}
                      className="px-3 py-2 bg-yellow-600 text-white rounded-lg font-semibold hover:bg-yellow-700 transition flex items-center gap-2"
                      title="Download PDF"
                    >
                      <FaDownload size={16} />
                    </button>
                  )}
                </div>

                <div className="max-h-[500px] overflow-y-auto overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm sm:text-base divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                          Target (KG)
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                          Reward
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dealerRewards.length > 0 ? (
                        dealerRewards.map((reward) => (
                          <tr
                            key={`${reward.fromKg}-${reward.toKg}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                              {reward.toKg
                                ? `${reward.fromKg.toLocaleString()} – ${reward.toKg.toLocaleString()} KG`
                                : `${reward.fromKg.toLocaleString()}+ KG`}
                            </td>
                            <td className="px-4 py-3 text-yellow-700 font-semibold">
                              {reward.rewards.join(" / ")}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={2}
                            className="px-4 py-3 text-center text-gray-500"
                          >
                            No rewards found for this dealer.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ADD/EDIT USER */}
          {currentView === "addUser" && (
            <AddUser
              editUser={editingUser}
              onSaved={() => fetchDealerDashboard()}
              onClose={() => {
                setEditingUser(null);
                setCurrentView("users");
              }}
            />
          )}

          {/* USERS LIST */}
          {currentView === "users" && (
            <UsersList
              onEditUser={handleEditUserFromList}
              onAddSale={openAddSaleForUser}
            />
          )}

          {/* SALES LIST */}
          {currentView === "sales" && <SalesList />}
        </div>
      </main>

      {/* ADD SALE MODAL */}
      {showAddSale && (
        <AddSale
          users={usersForSale}
          preselectedUser={preSelectedUser}
          onClose={() => {
            setShowAddSale(false);
            setPreSelectedUser(null);
            setUsersForSale(dealerData.users || []);
          }}
          onSaleAdded={fetchSales}
        />
      )}
    </div>
  );
}
